import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, ScatterChart, Scatter, ZAxis } from 'recharts';
import { Moon, AlertCircle, Clock, TrendingUp, Calendar } from 'lucide-react';
import { BathroomVisit } from '../BathroomSafety';

// Mock data generation - similar to the one in BathroomVisitAnalytics but focusing on nighttime
const generateMockNighttimeData = (): BathroomVisit[] => {
  const visits: BathroomVisit[] = [];
  const now = new Date();
  
  // Generate data for the past 30 days
  for (let i = 0; i < 30; i++) {
    const day = new Date();
    day.setDate(now.getDate() - i);
    
    // Generate 0-3 nighttime visits per day
    const visitsPerDay = Math.floor(Math.random() * 4);
    for (let j = 0; j < visitsPerDay; j++) {
      // Nighttime hours only (10PM - 6AM)
      const hour = Math.random() < 0.5 ? 
                   Math.floor(Math.random() * 7) : // 0-6 AM
                   22 + Math.floor(Math.random() * 2); // 10-11 PM
      const minute = Math.floor(Math.random() * 60);
      
      const timestamp = new Date(day);
      timestamp.setHours(hour, minute);
      
      // Duration between 1 and 10 minutes (in seconds)
      const duration = (1 + Math.floor(Math.random() * 9)) * 60;
      
      // Higher probability of issues during late night
      const problemProbability = hour >= 0 && hour <= 4 ? 0.4 : 0.2;
      
      visits.push({
        id: `night-visit-${i}-${j}`,
        timestamp,
        duration,
        movementPattern: Math.random() < problemProbability ? 
                         (Math.random() < 0.6 ? 'unstable' : Math.random() < 0.5 ? 'slow' : 'rapid') : 
                         'normal',
        pressureDistribution: Math.random() < problemProbability ? 
                              (Math.random() < 0.7 ? 'unbalanced' : 'shifting') : 
                              'balanced',
        waterDetected: Math.random() < 0.15,
        exitStatus: Math.random() < problemProbability ? 
                    (Math.random() < 0.7 ? 'assisted' : 'emergency') : 
                    'normal',
        isNighttime: true
      });
    }
  }
  
  // Sort by timestamp
  return visits.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
};

const mockNighttimeVisits = generateMockNighttimeData();

// Helper to get hour part only from Date
const getHour = (date: Date): number => {
  return date.getHours() + date.getMinutes() / 60;
};

// Prepare data for time distribution chart
const prepareHourDistribution = (visits: BathroomVisit[]) => {
  // Group by hour
  const hourCounts: { [key: number]: number } = {};
  
  visits.forEach(visit => {
    const hour = visit.timestamp.getHours();
    hourCounts[hour] = (hourCounts[hour] || 0) + 1;
  });
  
  // Convert to chart data format
  return Object.keys(hourCounts).map(hourStr => {
    const hour = parseInt(hourStr);
    const formattedHour = hour < 10 ? `0${hour}:00` : `${hour}:00`;
    return {
      hour: formattedHour,
      count: hourCounts[hour],
      risk: hour >= 0 && hour <= 4 ? 'high' : 'medium'
    };
  }).sort((a, b) => {
    // Special sort to keep nighttime hours in sequence (10PM-6AM)
    const hourA = parseInt(a.hour.split(':')[0]);
    const hourB = parseInt(b.hour.split(':')[0]);
    
    // Convert to 0-8 scale where 0 = 10PM, 8 = 6AM
    const valueA = hourA >= 22 ? hourA - 22 : hourA + 2;
    const valueB = hourB >= 22 ? hourB - 22 : hourB + 2;
    
    return valueA - valueB;
  });
};

// Prepare data for trends over time
const prepareWeeklyTrends = (visits: BathroomVisit[]) => {
  // Group by week
  const weeklyData: { [key: string]: { count: number, issues: number } } = {};
  
  visits.forEach(visit => {
    const date = visit.timestamp;
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay());
    const weekKey = weekStart.toISOString().split('T')[0];
    
    if (!weeklyData[weekKey]) {
      weeklyData[weekKey] = { count: 0, issues: 0 };
    }
    
    weeklyData[weekKey].count++;
    
    if (visit.movementPattern !== 'normal' || 
        visit.pressureDistribution !== 'balanced' || 
        visit.exitStatus !== 'normal') {
      weeklyData[weekKey].issues++;
    }
  });
  
  // Convert to chart data format
  return Object.keys(weeklyData)
    .sort()
    .slice(-5) // Last 5 weeks
    .map(week => {
      const date = new Date(week);
      return {
        week: `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
        visits: weeklyData[week].count,
        issues: weeklyData[week].issues
      };
    });
};

// Prepare data for scatter plot
const prepareScatterData = (visits: BathroomVisit[]) => {
  return visits.map(visit => ({
    hour: getHour(visit.timestamp),
    duration: visit.duration / 60, // Convert to minutes
    date: visit.timestamp.toISOString().split('T')[0],
    status: visit.exitStatus === 'emergency' ? 'emergency' : 
            visit.exitStatus === 'assisted' ? 'assisted' : 'normal'
  }));
};

// Calculate health risk based on nighttime visits
const calculateHealthRisk = (visits: BathroomVisit[]) => {
  if (visits.length === 0) return { level: 'low', score: 0 };
  
  // Calculate average number of nighttime visits per night
  const uniqueDays = new Set(visits.map(v => v.timestamp.toISOString().split('T')[0])).size;
  const avgVisitsPerNight = visits.length / uniqueDays;
  
  // Calculate percentage of problematic visits
  const problematicVisits = visits.filter(v => 
    v.movementPattern !== 'normal' || 
    v.pressureDistribution !== 'balanced' || 
    v.exitStatus !== 'normal'
  ).length;
  
  const problemPercentage = (problematicVisits / visits.length) * 100;
  
  // Calculate time clustering (visits close together in time)
  let clusterCount = 0;
  for (let i = 1; i < visits.length; i++) {
    const timeDiff = visits[i].timestamp.getTime() - visits[i-1].timestamp.getTime();
    const sameDay = visits[i].timestamp.toDateString() === visits[i-1].timestamp.toDateString();
    if (sameDay && timeDiff < 2 * 60 * 60 * 1000) { // Less than 2 hours apart
      clusterCount++;
    }
  }
  
  const clusterRatio = clusterCount / visits.length;
  
  // Calculate overall risk score (0-100)
  let score = 0;
  score += avgVisitsPerNight > 3 ? 40 : avgVisitsPerNight > 2 ? 25 : avgVisitsPerNight > 1 ? 10 : 0;
  score += Math.min(40, problemPercentage * 0.4);
  score += clusterRatio * 20;
  
  // Determine risk level
  let level: 'low' | 'moderate' | 'high' | 'severe' = 'low';
  if (score >= 70) level = 'severe';
  else if (score >= 50) level = 'high';
  else if (score >= 30) level = 'moderate';
  
  return { level, score: Math.round(score) };
};

// Format time for display
const formatTime = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const NighttimeVisitTracking: React.FC = () => {
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter'>('month');
  
  // Filter visits based on selected time range
  const filteredVisits = mockNighttimeVisits.filter(visit => {
    const now = new Date();
    const timeLimit = new Date();
    
    if (timeRange === 'week') {
      timeLimit.setDate(now.getDate() - 7);
    } else if (timeRange === 'month') {
      timeLimit.setDate(now.getDate() - 30);
    } else {
      timeLimit.setDate(now.getDate() - 90);
    }
    
    return visit.timestamp >= timeLimit;
  });
  
  const hourDistribution = prepareHourDistribution(filteredVisits);
  const weeklyTrends = prepareWeeklyTrends(filteredVisits);
  const scatterData = prepareScatterData(filteredVisits);
  const healthRisk = calculateHealthRisk(filteredVisits);
  
  // Calculate simple statistics
  const totalVisits = filteredVisits.length;
  const avgPerNight = totalVisits / (timeRange === 'week' ? 7 : timeRange === 'month' ? 30 : 90);
  const mostCommonHour = hourDistribution.sort((a, b) => b.count - a.count)[0]?.hour || 'N/A';
  
  return (
    <div className="p-6">
      {/* Time Range Selector */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex space-x-2">
          <button
            className={`px-4 py-2 rounded-lg ${
              timeRange === 'week' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'
            }`}
            onClick={() => setTimeRange('week')}
          >
            Past Week
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${
              timeRange === 'month' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'
            }`}
            onClick={() => setTimeRange('month')}
          >
            Past Month
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${
              timeRange === 'quarter' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'
            }`}
            onClick={() => setTimeRange('quarter')}
          >
            Past 3 Months
          </button>
        </div>
        
        <div className={`px-4 py-2 rounded-full text-sm font-medium ${
          healthRisk.level === 'low' ? 'bg-green-100 text-green-800' :
          healthRisk.level === 'moderate' ? 'bg-yellow-100 text-yellow-800' :
          healthRisk.level === 'high' ? 'bg-orange-100 text-orange-800' :
          'bg-red-100 text-red-800'
        }`}>
          <span className="font-bold">Health Risk:</span> {healthRisk.level.charAt(0).toUpperCase() + healthRisk.level.slice(1)} ({healthRisk.score}/100)
        </div>
      </div>
      
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-indigo-50 p-4 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-indigo-900">Total Nighttime Visits</h3>
              <p className="text-3xl font-bold text-indigo-700">{totalVisits}</p>
            </div>
            <Moon className="text-indigo-500" />
          </div>
          <p className="mt-2 text-sm text-indigo-600">
            During the past {timeRange === 'week' ? '7 days' : timeRange === 'month' ? '30 days' : '90 days'}
          </p>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-purple-900">Average Per Night</h3>
              <p className="text-3xl font-bold text-purple-700">{avgPerNight.toFixed(1)}</p>
            </div>
            <Calendar className="text-purple-500" />
          </div>
          <p className="mt-2 text-sm text-purple-600">
            {avgPerNight > 2 ? 'Higher than recommended' : 'Within normal range'}
          </p>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-blue-900">Most Common Time</h3>
              <p className="text-3xl font-bold text-blue-700">{mostCommonHour}</p>
            </div>
            <Clock className="text-blue-500" />
          </div>
          <p className="mt-2 text-sm text-blue-600">
            {mostCommonHour.startsWith('0') && parseInt(mostCommonHour) < 5 ? 
             'Deep sleep disruption potential' : 'Less disruptive timing'}
          </p>
        </div>
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-4">Nighttime Visit Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={hourDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Bar 
                dataKey="count" 
                name="Visits" 
                fill="#6366F1" 
                radius={[4, 4, 0, 0]}
              >
                {hourDistribution.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.risk === 'high' ? '#EF4444' : '#6366F1'} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="mt-3 text-sm text-gray-600">
            <span className="inline-block w-3 h-3 bg-red-500 mr-1"></span> High risk hours (midnight to 4AM)
            <span className="inline-block w-3 h-3 bg-indigo-500 ml-3 mr-1"></span> Other nighttime hours
          </p>
        </div>
        
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-4">Weekly Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={weeklyTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="visits" 
                name="Total Visits" 
                stroke="#6366F1" 
                strokeWidth={2} 
                activeDot={{ r: 8 }}
              />
              <Line 
                type="monotone" 
                dataKey="issues" 
                name="Problematic Visits" 
                stroke="#EF4444" 
                strokeWidth={2} 
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Nighttime Visit Pattern Analysis */}
      <div className="bg-white rounded-lg p-4 shadow-sm mb-6">
        <h3 className="text-lg font-medium mb-4">Visit Timing & Duration Analysis</h3>
        <div className="mb-3">
          <p className="text-sm text-gray-600">
            This chart shows the distribution of nighttime bathroom visits across the night, with visit duration and potential issues indicated.
          </p>
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <CartesianGrid />
            <XAxis 
              type="number" 
              dataKey="hour" 
              name="Time" 
              domain={[21, 7]} 
              tickFormatter={(hour) => {
                const h = hour % 24;
                return `${h < 10 ? '0' : ''}${h}:00`;
              }}
              label={{ value: 'Time of Night', position: 'insideBottom', offset: -10 }}
            />
            <YAxis 
              type="number" 
              dataKey="duration" 
              name="Duration" 
              label={{ value: 'Duration (minutes)', angle: -90, position: 'insideLeft' }}
            />
            <ZAxis range={[60, 60]} />
            <Tooltip 
              formatter={(value, name) => {
                if (name === 'Time') {
                  const hour = Math.floor(value as number);
                  const minute = Math.round(((value as number) % 1) * 60);
                  return [`${hour < 10 ? '0' : ''}${hour}:${minute < 10 ? '0' : ''}${minute}`, name];
                }
                return [value, name];
              }}
              itemSorter={(item) => item.value !== undefined ? -item.value as number : 0}
            />
            <Legend />
            <Scatter 
              name="Normal Visits" 
              data={scatterData.filter(d => d.status === 'normal')} 
              fill="#6366F1" 
              shape="circle"
            />
            <Scatter 
              name="Assisted Exits" 
              data={scatterData.filter(d => d.status === 'assisted')} 
              fill="#F59E0B" 
              shape="triangle"
            />
            <Scatter 
              name="Emergency Situations" 
              data={scatterData.filter(d => d.status === 'emergency')} 
              fill="#EF4444" 
              shape="star"
            />
          </ScatterChart>
        </ResponsiveContainer>
        <div className="mt-4">
          <h4 className="font-medium text-gray-900 mb-2">Health Implications</h4>
          <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
            <li><span className="font-medium">Multiple visits per night</span> may indicate urinary tract issues, diabetes, or medication side effects.</li>
            <li><span className="font-medium">Visits between 2-4 AM</span> are more disruptive to deep sleep and increase fall risk.</li>
            <li><span className="font-medium">Longer durations</span> during the night may indicate difficulty with mobility or balance in low-light conditions.</li>
            <li><span className="font-medium">Increasing frequency</span> over time should be reported to healthcare providers.</li>
          </ul>
        </div>
      </div>
      
      {/* Health Recommendations */}
      <div className="bg-indigo-50 rounded-lg p-4">
        <h3 className="text-lg font-medium text-indigo-900 mb-3">Recommendations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-3 rounded-lg">
            <h4 className="font-medium text-indigo-800 mb-2">Environmental Adjustments</h4>
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
              <li>Install nightlights along the path to the bathroom</li>
              <li>Consider motion-activated lighting that triggers when getting out of bed</li>
              <li>Ensure clear pathways free of obstacles</li>
              <li>Use anti-slip mats in the bathroom</li>
            </ul>
          </div>
          <div className="bg-white p-3 rounded-lg">
            <h4 className="font-medium text-indigo-800 mb-2">Medical Considerations</h4>
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
              <li>Review medications with doctor, especially diuretics</li>
              <li>Consider screening for diabetes or urinary tract issues</li>
              <li>Limit fluid intake 2-3 hours before bedtime</li>
              <li>Discuss sleep quality with healthcare provider</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NighttimeVisitTracking; 